/**
 * 본부 — 매장 구독 요금 입력 (정가 · 할인 금액 · 할인 이름) (2026-10-28 대표 지시)
 *
 * "HQ 에서 승인하거나 발급할 때 구독 요금에 할인 금액 적용 가능하게."
 * 쓰는 곳: 📝 구독 동의 현황 [확인] · ✅ 가입 승인 · 🧾 월 사용료 매장 설정.
 * 서버에는 { list_price, monthly_fee(=정가−할인, 할인 후 실제 월액), discount_label } 로 보낸다.
 * 금액은 전부 공급가액(부가세 별도).
 */
import React from 'react';

export interface HqPriceValue {
  list_price: string;
  discount_amount: string;
  discount_label: string;
}

export const BASIC_LIST_PRICE = 500000;
export const DEFAULT_PROMO_LABEL = '수강생 프로모션';

const toInt = (v: string) => {
  const n = parseInt((v || '').replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
};

/** 입력값 → 서버 본문. 할인 후 금액이 0 이하면 오류 문구를 돌려준다. */
export const priceToBody = (v: HqPriceValue): { body?: { list_price: number; monthly_fee: number; discount_label: string | null }; error?: string } => {
  const list = toInt(v.list_price);
  const discount = toInt(v.discount_amount);
  if (!list) return { error: '정가를 넣어 주세요' };
  if (discount > list) return { error: '할인 금액이 정가보다 큽니다' };
  const fee = list - discount;
  if (fee <= 0) return { error: '할인 후 금액이 0원입니다 — 받지 않을 달은 월 사용료 화면에서 [면제]로 처리해 주세요' };
  return { body: { list_price: list, monthly_fee: fee, discount_label: discount ? v.discount_label.trim() || '할인' : null } };
};

/** 서버의 price(정가·할인·실제 금액) → 입력값 */
export const priceToValue = (p?: { list_price: number; discount_amount: number; discount_label: string | null } | null): HqPriceValue => ({
  list_price: String(p?.list_price ?? BASIC_LIST_PRICE),
  discount_amount: String(p?.discount_amount ?? 0),
  discount_label: p?.discount_label ?? '',
});

const HqPriceInput: React.FC<{ value: HqPriceValue; onChange: (v: HqPriceValue) => void; compact?: boolean }> = ({
  value,
  onChange,
  compact,
}) => {
  const list = toInt(value.list_price);
  const discount = toInt(value.discount_amount);
  const fee = list - discount;
  const cls = 'px-2 py-1.5 border border-slate-300 rounded-md text-sm';
  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-3 ${compact ? 'text-xs' : 'text-sm'}`}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">정가(월, 원)</span>
          <input
            inputMode="numeric"
            className={`${cls} w-28`}
            value={value.list_price}
            onChange={(e) => onChange({ ...value, list_price: e.target.value.replace(/[^0-9]/g, '') })}
          />
        </label>
        <span className="pb-2 text-slate-400">−</span>
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">할인 금액(원)</span>
          <input
            inputMode="numeric"
            className={`${cls} w-28`}
            value={value.discount_amount}
            onChange={(e) => onChange({ ...value, discount_amount: e.target.value.replace(/[^0-9]/g, '') })}
          />
        </label>
        <label className="flex flex-col gap-1 flex-1 min-w-[10rem]">
          <span className="text-slate-500">할인 이름</span>
          <input
            maxLength={50}
            className={cls}
            placeholder={DEFAULT_PROMO_LABEL}
            value={value.discount_label}
            onChange={(e) => onChange({ ...value, discount_label: e.target.value })}
          />
        </label>
      </div>
      <p className="mt-2 text-slate-700">
        = 할인 후 <b className={fee > 0 ? 'text-slate-900' : 'text-red-600'}>월 {Math.max(0, fee).toLocaleString('ko-KR')}원</b>
        <span className="text-slate-400"> (부가세 별도 · 청구서에 이 금액이 나갑니다)</span>
        <button
          type="button"
          className="ml-2 text-blue-600 underline"
          onClick={() => onChange({ list_price: String(BASIC_LIST_PRICE), discount_amount: '200000', discount_label: DEFAULT_PROMO_LABEL })}
        >
          기존 매장 30만원으로
        </button>
      </p>
    </div>
  );
};

export default HqPriceInput;
